import type { PaginatedResponse } from '@/shared/api/types/api-response'
import type {
  CreateVoucherPayload,
  IVoucher,
  UpdateVoucherPayload,
  VoucherListParams,
} from '@/shared/api/types/voucher-types'

let MOCK_ID_COUNTER = 4

const MOCK_VOUCHERS: IVoucher[] = [
  {
    id: '1',
    tenantId: 'tenant-1',
    code: 'TOUR10',
    name: 'Giảm 10% cho tour',
    discountPercent: 10,
    quantity: 100,
    startDate: '2025-01-01',
    endDate: '2025-12-31',
    status: 'AVAILABLE',
    type: 'TOUR',
    applicableTourIds: ['tour-1', 'tour-2'],
    applicablePoiIds: [],
    issuedCount: 45,
    usedCount: 12,
    createdAt: '2025-01-01T00:00:00Z',
    updatedAt: '2025-06-01T00:00:00Z',
  },
  {
    id: '2',
    tenantId: 'tenant-1',
    code: 'POI20',
    name: 'Giảm 20% tại điểm tham quan',
    discountPercent: 20,
    quantity: 50,
    startDate: '2025-03-01',
    endDate: '2025-09-30',
    status: 'AVAILABLE',
    type: 'POI',
    applicableTourIds: [],
    applicablePoiIds: ['poi-1'],
    issuedCount: 20,
    usedCount: 8,
    createdAt: '2025-03-01T00:00:00Z',
    updatedAt: '2025-05-15T00:00:00Z',
  },
  {
    id: '3',
    tenantId: 'tenant-1',
    code: 'SURVEY15',
    name: 'Khảo sát hoàn thành tour - Giảm 15%',
    discountPercent: 15,
    quantity: 200,
    startDate: '2025-01-01',
    endDate: '2025-06-30',
    status: 'UNAVAILABLE',
    type: 'TOUR',
    applicableTourIds: ['tour-3'],
    applicablePoiIds: [],
    issuedCount: 180,
    usedCount: 95,
    createdAt: '2025-01-01T00:00:00Z',
    updatedAt: '2025-04-20T00:00:00Z',
  },
]

const store = new Map<string, IVoucher>(MOCK_VOUCHERS.map((v) => [v.id, v]))

async function delay(ms = 300): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export async function getVouchers(
  _tenantId: string,
  params: VoucherListParams,
): Promise<PaginatedResponse<IVoucher>> {
  await delay()
  let items = Array.from(store.values())
  if (params.status) items = items.filter((v) => v.status === params.status)
  if (params.type) items = items.filter((v) => v.type === params.type)
  const total = items.length
  const start = (params.current - 1) * params.pageSize
  const data = items.slice(start, start + params.pageSize)
  return { data, total, current: params.current, pageSize: params.pageSize, keyword: null }
}

export async function getVoucherById(id: string): Promise<IVoucher> {
  await delay()
  const voucher = store.get(id)
  if (!voucher) throw new Error('Voucher not found')
  return voucher
}

export async function createVoucher(payload: CreateVoucherPayload): Promise<IVoucher> {
  await delay()
  const now = new Date().toISOString()
  const voucher: IVoucher = {
    id: String(++MOCK_ID_COUNTER),
    tenantId: payload.tenantId,
    code: payload.code,
    name: payload.name,
    discountPercent: payload.discountPercent,
    quantity: payload.quantity,
    startDate: payload.startDate,
    endDate: payload.endDate,
    status: payload.status,
    type: payload.type,
    applicableTourIds: payload.applicableTourIds,
    applicablePoiIds: payload.applicablePoiIds,
    issuedCount: 0,
    usedCount: 0,
    createdAt: now,
    updatedAt: now,
  }
  store.set(voucher.id, voucher)
  return voucher
}

export async function updateVoucher(id: string, payload: UpdateVoucherPayload): Promise<IVoucher> {
  await delay()
  const existing = store.get(id)
  if (!existing) throw new Error('Voucher not found')
  const updated: IVoucher = { ...existing, ...payload, updatedAt: new Date().toISOString() }
  store.set(id, updated)
  return updated
}

export async function deleteVoucher(id: string): Promise<void> {
  await delay()
  store.delete(id)
}
